import { LitElement, html } from 'lit';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit.min.js';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { nothing } from 'lit-html';
import { DebugService } from '../../../shared/services/debug.service.js';
import { stratum } from '../../../../constants/stratum.js';
import { style } from './iconic-choose-loan.style.js';
import { REFI_VEHICLE_LOAN_CONST } from '../../../shared/constants/app.constants.js';
import { tags } from '../../../shared/constants/tags.js';
import { VehicleService } from '../../../services/vehicle.service.js';
import { LoanService } from '../../../services/loan.service.js';
import { SessionService } from '../../../core/services/session/session.service.js';
import '../../../shared/modals/papr-limit/papr-limit-modal.component.js';
import '../iconic-loan-card/iconic-loan-card.component.js';
import '../iconic-manual-loan/iconic-manual-loan.component.js';
import '../iconic-loan-warning/iconic-loan-warning.component.js';
import { CookieService } from '../../../core/services/cookie/cookie.service.js';
import { VehicleAndLoanService } from '../../../services/vehicle-and-loan.service.js';
import { ApplicationService } from '../../../services/application.service.js';
import '../../iconic-container/iconic-container.component.js';
import { POLICY_LIMITS_CONST } from '../../../shared/services/policy-limits.service.js';

export class RefiIconicChooseLoan extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicChooseLoan');

    this.showLoadingIcon = false;

    this.manualLoanModel = {
      paymentDueDay: null,
      monthlyPayment: null,
      currentBalance: null,
      remainingTerm: null,
      isCapitalOneLoaned: '',
      isLease: '',
    };

    this.hasLoans = true;

    this.isLoanEligible = true;
    this.isVehicleEligible = true;
    this.marketingOffer = ApplicationService.MarketingOffer;
  }

  static properties = {
    showLoadingIcon: { type: Boolean },
    callingCustomerLoans: { type: Boolean },
    isCaponeLoan: { type: Boolean },
    hasLoans: { type: Boolean },
    useManualEntry: { type: Boolean },
    selectedMatchedVehicle: { type: Boolean },
    usedLicensePlateSearch: { type: Boolean },
    manualLoanModel: { type: Object },
    paprModal: { type: Object },
    errorMessage: { type: String },
    selectedOffer: { type: Object },
  };

  firstUpdated() {
    if (this.hasLoans && !this.useManualEntry) {
      stratum.publishTag(
        ApplicationService.isInAppDeclineDelayOn
          ? TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_VEHICLE_LOAN_LOAN_2
          : TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_VEHICLE_LOAN_LOAN
      );
    }
  }

  static styles = [style, fsTypography];

  render() {
    const loanElements = [];
    for (let i = 0; i < this.loansList.length; i += 1) {
      this.debug.log(`rendering loan ${this.loansList[i].lenderName}`);
      loanElements.push(html`
        <div class="loan__loans">
          <refi-iconic-loan-card
            .loan="${this.loansList[i]}"
            .loanIndex="${i}"
            @selectedLoan="${(ev) => this.setSelectedLoan(ev)}"
            @goToReviewStep="${(ev) => this.selectLoan(ev)}"
            showButton
            hide-ineligibility="${ApplicationService.isInAppDeclineDelayOn
              ? ''
              : nothing}"
          ></refi-iconic-loan-card>
        </div>
      `);
    }
    this.debug.log('rendered');
    this.debug.log(
      `has loans? ${this.hasLoans}, used manual entry? ${this.useManualEntry}`
    );
    return html`
      <refi-track-pageview pagename="loan"></refi-track-pageview>
      <refi-iconic-container
        headerText="${LoanService.getSelectLoanHeader(this.useManualEntry)}"
        .marketingOffer=${this.marketingOffer}
        data-e2e-ref="chooseLoanContainer"
      >
        ${!this.callingCustomerLoans && !this.isCaponeLoan
          ? html`
              <refi-papr-limit-modal
                id="refi-papr-limit"
                @continuePrequal="${this.goToNextStep}"
                @exitPrequal="${this.exitPrequal}"
              ></refi-papr-limit-modal>
              <div>
                <refi-iconic-vehicle-loan-card
                  .vehicle="${this.vehicle}"
                  .showPlaceholder="${true}"
                  .showButton="${false}"
                  .canEdit="${true}"
                  hide-ineligibility="${ApplicationService.isInAppDeclineDelayOn
                    ? ''
                    : nothing}"
                  .tagBalance="${ApplicationService.isInAppDeclineDelayOn}"
                ></refi-iconic-vehicle-loan-card>
                ${this.hasLoans && !this.useManualEntry
                  ? html`
                      <h2 class="loan-header">Select a loan</h2>
                      <div class="grid">${loanElements}</div>
                      <div class="return-cta">
                        <fs-button
                          class="return-cta-button"
                          @click="${this.toggleManualInputs}"
                          tabindex="0"
                          data-track="loan manual entry"
                          ghost
                          compact
                          data-e2e-ref="return-cta"
                          >I want to refinance a different loan</fs-button
                        >
                      </div>
                    `
                  : ''}
                ${!this.hasLoans || this.useManualEntry
                  ? html`
                      <div class="loan-header">Enter a loan</div>
                      <refi-iconic-manual-loan
                        .loanObject="${this.manualLoanModel}"
                        @update-manual-loan-object="${(ev) =>
                          this.updateManualLoanObject(ev)}"
                        @loanFormValidity="${(ev) => this.goToNextStep(ev)}"
                      ></refi-iconic-manual-loan>
                      ${this.loansList.length !== 0
                        ? html`<div class="return-cta">
                            <fs-button
                              class="return-cta-button"
                              @click="${this.toggleManualInputs}"
                              tabindex="0"
                              data-track="loan manual entry"
                              ghost
                              compact
                              >Go back to loan options</fs-button
                            >
                          </div>`
                        : ''}
                    `
                  : ''}
              </div>
            `
          : ''}
        ${this.isCaponeLoan
          ? html` <div class="warning-container">
              <refi-iconic-loan-warning></refi-iconic-loan-warning>
            </div>`
          : ''}
      </refi-iconic-container>
    `;
  }

  connectedCallback() {
    super.connectedCallback();

    this.vehicle = VehicleService.selectedVehicle;
    this.vehicleAndLoanMatchList =
      VehicleAndLoanService.vehicleAndLoanMatchList;
    this.selectedMatchedVehicle = false;
    this.checkIfSelectedVehicleIsMatched();
    this.usedLicensePlateSearch =
      VehicleService.lookupType === 'licensePlateSearch';
    this.manualLoanModel = LoanService.getManualLoanModel();
    this.callingCustomerLoans = false;
    this.setLoansList();
    Object.keys(RefiIconicChooseLoan.properties).forEach((prop) =>
      this.debug.log(`${prop} ${this[prop]}`)
    );
    this.sendHostAppHeaderUpdates();
    setTimeout(() => {
      window.scroll({ top: -1, left: 0 });
    }, 1);
  }

  setLoansList() {
    this.loansList = LoanService.getLoansList();
    this.hasLoans = this.loansList.length > 0;
    this.showLoadingIcon = false;
  }

  checkIfSelectedVehicleIsMatched() {
    if (!this.vehicle?.vin || ApplicationService.isInAppDeclineDelayOn) {
      return;
    }
    for (const match of this.vehicleAndLoanMatchList) {
      if (match.vehicle.vin === this.vehicle.vin) {
        this.match = match;
        this.selectedMatchedVehicle = true;
        this.setSelectedLoan({ detail: { loan: match.loan } }, false);
        this.setSelectedVehicle({ detail: { vehicle: match.vehicle } });
        this.selectLoan(true);
      }
    }
  }

  toggleManualInputs() {
    if (this.useManualEntry) {
      stratum.publishTag(
        ApplicationService.isInAppDeclineDelayOn
          ? TagKey.ICONIC_VEHICLE_AND_LOAN_GO_BACK_TO_LOAN_OPTIONS_2
          : TagKey.ICONIC_VEHICLE_AND_LOAN_GO_BACK_TO_LOAN_OPTIONS
      );
    } else if (ApplicationService.isInAppDeclineDelayOn) {
      stratum.publishTag(TagKey.ICONIC_VEHICLE_AND_LOAN_CHANGE_LOAN_LINK);
    }
    REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.loan.changeLoanSelected);
    this.useManualEntry = !this.useManualEntry;
    setTimeout(() => {
      window.scroll({ top: -1, left: 0 });
    }, 1);
  }

  updateManualLoanObject(ev) {
    this.manualLoanModel = ev.detail;
  }

  setSelectedLoan(ev, sendAnalytics = true) {
    this.debug.log('setting selected loan');
    window.scrollTo(0, 0);
    const { loan } = ev.detail;
    this.selectedLoan = loan;
    this.selectedLoan.paymentDueDay = 15;
    this.selectedLoan.isCapitalOneLoaned = !loan.lenderName ? '' : 'no';
    this.selectedLoan.isLease = '';
    if (loan.lenderAccountNumber) {
      this.selectedLoan.lenderAccountNumber = loan.lenderAccountNumber;
    }
    if (loan.lenderName) {
      this.selectedLoan.lenderName = loan.lenderName;
    }
    this.useManualEntry = false;
    if (sendAnalytics) {
      REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.loan.selected(loan.lenderName));
    }
  }

  selectLoan(ev) {
    const promo = SessionService.getPromotion();
    if (
      this.selectedLoan.apr < POLICY_LIMITS_CONST.PAPR_MINIMUM() &&
      promo == null &&
      ev.detail !== true
    ) {
      // does not affect manual loan entry customers
      const paprModal = this.shadowRoot.getElementById('refi-papr-limit');
      paprModal.openModal(this.selectedLoan, null);
    } else {
      this.goToNextStep(ev.detail);
    }
  }

  setSelectedVehicle(ev) {
    const { vehicle } = ev.detail;
    this.isVehicleSelected = false;
    this.debug.log('selectedVehicle', vehicle);
    this.selectedVehicle = vehicle;
    VehicleService.selectedVehicle = vehicle;
    this.isVehicleSelected = true;
  }

  goToNextStep(ev) {
    this.vehicle = VehicleService.selectedVehicle;
    const isManualLoan = ev?.detail?.isManualLoan;
    if (
      (!ApplicationService.isInAppDeclineDelayOn &&
        isManualLoan &&
        this.manualLoanModel.isCapitalOneLoaned === 'yes') ||
      (this.selectedLoan && this.selectedLoan.isCapitalOneLoaned === 'yes')
    ) {
      REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.shared.loan.submittedYes);
      stratum.publishTag(TagKey.MANUAL_LOAN_ENTRY_CAPITAL_ONE_LOAN, {
        replacements: {
          value: 'yes',
        },
      });
      LoanService.setUseManualLoanModel(isManualLoan);
      LoanService.saveLoanPrequalInfo(
        `We only refinance cars that aren't currently financed by Capital One.`
      );
      return;
    }

    if (
      (!ApplicationService.isInAppDeclineDelayOn &&
        isManualLoan &&
        this.manualLoanModel.isLease === 'yes') ||
      (this.selectedLoan && this.selectedLoan.isLease === 'yes')
    ) {
      REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.shared.loan.submittedYesLease);
      stratum.publishTag(TagKey.MANUAL_LOAN_ENTRY_LEASE, {
        replacements: {
          value: 'yes',
        },
      });
      LoanService.setUseManualLoanModel(isManualLoan);
      LoanService.saveLoanPrequalInfo(`We do not refinance leases.`);
      return;
    }

    if (isManualLoan) {
      LoanService.setUseManualLoanModel(true);
      LoanService.saveManualLoanModelToSession(this.manualLoanModel);
      this.useManualEntry = isManualLoan;
      REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
        tags.shared.loan.submittedNo,
        '',
        'msg',
        'message'
      );
      if (!ApplicationService.isInAppDeclineDelayOn) {
        stratum.publishTag(TagKey.MANUAL_LOAN_ENTRY_CAPITAL_ONE_LOAN, {
          replacements: {
            value: 'no',
          },
        });
        stratum.publishTag(TagKey.MANUAL_LOAN_ENTRY_LEASE, {
          replacements: {
            value: 'no',
          },
        });
      } else {
        stratum.publishTag(TagKey.ICONIC_VEHICLE_AND_LOAN_EDITED_FIELD, {
          replacements: {
            value: `COF Loan ${
              this.manualLoanModel.isCapitalOneLoaned &&
              this.manualLoanModel.isCapitalOneLoaned.toLowerCase() === 'yes'
                ? 'Yes'
                : 'No'
            }`,
          },
        });
        stratum.publishTag(TagKey.ICONIC_VEHICLE_AND_LOAN_EDITED_FIELD, {
          replacements: {
            value: `Lease ${
              this.manualLoanModel.isLease &&
              this.manualLoanModel.isLease.toLowerCase() === 'yes'
                ? 'Yes'
                : 'No'
            }`,
          },
        });
        for (const key of Object.keys(this.manualLoanModel)) {
          if (
            ![
              'isCapitalOneLoaned',
              'ineligibleReasons',
              'paymentDueDay',
              'isLease',
            ].includes(key)
          ) {
            stratum.publishTag(TagKey.ICONIC_VEHICLE_AND_LOAN_EDITED_FIELD, {
              replacements: {
                value: `${key}`,
              },
            });
          }
        }
      }
    } else {
      if (ApplicationService.isInAppDeclineDelayOn) {
        stratum.publishTag(TagKey.TU_LOAN_SELECTION_TU_LOAN_SELECTED_2, {
          replacements: {
            value: `${this.selectedLoan.lenderName}`,
          },
        });
      } else {
        stratum.publishTag(TagKey.TU_LOAN_SELECTION_TU_LOAN_SELECTED, {
          replacements: { value: this.selectedLoan.lenderName },
        });
      }
      LoanService.setUseManualLoanModel(false);
      LoanService.setSelectedLoan(this.selectedLoan);
    }

    this.checkLtv(isManualLoan);
    LoanService.saveLoanPrequalInfo();
    this.determineNextPage();
  }

  checkLtv(isManualLoan) {
    if (!ApplicationService.isInAppDeclineDelayOn) {
      const wholesale = parseInt(this.vehicle.wholeSaleValue, 10);
      const currentBal = isManualLoan
        ? this.manualLoanModel.currentBalance
        : this.selectedLoan.currentBalance;
      const ltv = currentBal / wholesale;
      if (ltv >= 1.5) {
        REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
          tags.shared.loan.highLtv,
          Math.round(ltv * 100).toString(),
          'error',
          'message'
        );
        if (isManualLoan) {
          stratum.publishTag(TagKey.LOAN_LOOKUP_INELIGIBLE_LOAN, {
            replacements: {
              value: `LTV Error - ${Math.round(ltv * 100).toString()}`,
            },
          });
        } else {
          stratum.publishTag(TagKey.LOAN_LOOKUP_REASON_FOR_TU_INELIGIBLE_LOAN, {
            replacements: {
              value: `LTV Error - ${Math.round(ltv * 100).toString()}`,
            },
          });
        }
      }
    }
  }

  determineNextPage() {
    this.debug.log('route to review');
    this.dispatchEvent(
      new CustomEvent('go-to-review', {
        bubbles: true,
        composed: true,
      })
    );
  }

  createSelectedVehicleAndLoan() {
    const selectedVehicleFromCache =
      ApplicationService.Application.selectedVehicle;
    const selectedLoanFromService = ApplicationService.Application.selectedLoan;
    const stateFromPrimaryApplicant = ApplicationService.applicant.stateCode;

    const selectedVehicle = {
      wholesaleValueAmount: selectedVehicleFromCache.wholeSaleValue,
      year: selectedVehicleFromCache.year,
      state: stateFromPrimaryApplicant,
    };

    const selectedLoan = {
      lenderAccountNumber: selectedLoanFromService.lenderAccountNumber,
      lenderName: selectedLoanFromService.lenderName,
      isCapitalOneLoaned: selectedLoanFromService.isCapitalOneLoaned,
      currentBalanceAmount: selectedLoanFromService.currentBalance,
      remainingTerm: selectedLoanFromService.remainingTerm,
      monthlyPaymentAmount: selectedLoanFromService.monthlyPayment,
      apr: selectedLoanFromService.apr,
      lastPaymentDate: selectedLoanFromService.lastPaymentDate,
    };

    const selectedVehicleAndLoan = {
      vehicleDetails: selectedVehicle,
      loanDetails: selectedLoan,
      upfrontOffers: true,
      customerRefiId: CookieService.refiId,
    };

    return selectedVehicleAndLoan;
  }

  sendHostAppHeaderUpdates() {
    this.dispatchEvent(
      new CustomEvent('show-progress-bar', {
        detail: {
          displayBar: true,
        },
        composed: true,
        bubbles: true,
      })
    );
  }
}

customElements.define('refi-iconic-choose-loan', RefiIconicChooseLoan);
